// Project: GalaxyComplete
// Created: 9/28/20 by sammy
// File: GalaxyMigrateCommon

import * as React from "react";
import { GalaxyMigrateDeploymentDetails, GalaxyMigrateStorageConfig } from "gc-web-proto/galaxycompletepb/apipb/domainpb/galaxymigrate_pb";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { CMC_MIGRATION_SUBROUTE, DEPLOYMENT_DETAILS_SUBROUTE, DEPLOYMENT_SUBROUTE, getProjectSubRouteTemplate, PROJECT_SUBROUTE } from "../app/AppRoutes";
import { PortNumbers } from "gc-web-proto/galaxymigratepb/galaxy_migrate_pb";
import { BlockDeviceInfo } from "gc-web-proto/galaxymigratepb/galaxy_migrate_types_pb";
import { useAppServices } from "../app/services";
import { useAppGlobalState } from "../app/AppGlobalState";
import { MTDIDeploymentInfo } from "gc-web-proto/galaxycompletepb/apipb/domainpb/mtdi_pb";
import { useDownloadFileFromURL } from "../../common/misc";
import { DeploymentHostEnvironment } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/deployment_host_environment_pb";
import { useCurrentProjectID } from "../project/CurrentProjectState";
import { appI18n } from "../i18n/i18n";
import { HOST_PLATFORMS, hostPlatformFromEnvironment } from "../../common/HostPlatforms";

export const getGmStorageConfigDeviceTypeFromObject = (d: GalaxyMigrateStorageConfig.Device.AsObject) => {
    let deviceType = d.blockDevice?.deviceType;
    if (d.blockDevice?.fsType) {
        deviceType = `${deviceType} (${d.blockDevice.fsType})`;
    }
    return deviceType;
};
export const getGmStorageConfigDeviceType = (d: GalaxyMigrateStorageConfig.Device.AsObject) => {
    let deviceType = d.blockDevice.deviceType;
    if (d.blockDevice.fsType) {
        deviceType = `${deviceType} (${d.blockDevice.fsType})`;
    }
    return deviceType;
};

export const getGmStorageConfigDeviceFsType = (d: GalaxyMigrateStorageConfig.Device) => {
    return d.getBlockDevice().getFsType() || "--";
};

export const getGmStorageConfigDeviceFsInfo = (d: GalaxyMigrateStorageConfig.Device) => {
    let deviceType = d.getBlockDevice().getFsType() || "--";
    if (d.getBlockDevice().getLabel()) {
        deviceType = `${deviceType} | ${d.getBlockDevice().getLabel()}`;
    }
    if (d.getBlockDevice().getMountPoint()) {
        const mountPoint = d.getBlockDevice().getMountPoint();
        deviceType = `${deviceType} | ${mountPoint}`;
    }
    return deviceType;
};

export const getGmStorageConfigDeviceRoleLabel = (d: GalaxyMigrateStorageConfig.Device) => {
    switch (d.getRole()?.getRole()) {
        case GalaxyMigrateStorageConfig.Device.RoleInfo.Role.SOURCE:
            return appI18n.t("galaxymigrate:storage.deviceRoles.source");
        case GalaxyMigrateStorageConfig.Device.RoleInfo.Role.DESTINATION:
            return appI18n.t("galaxymigrate:storage.deviceRoles.destination");
        default:
            return appI18n.t("galaxymigrate:storage.deviceRoles.discovered");
    }
};

export const getGmStorageConfigDeviceMountUsageInfo = (d: GalaxyMigrateStorageConfig.Device): BlockDeviceInfo.MountPointUsageInfo | undefined => {
    return d.getBlockDevice().getMountPointUsageInfo();
};

export const getGmStorageConfigDeviceLegacyMountPoint = (d: GalaxyMigrateStorageConfig.Device) => {
    return d.getBlockDevice().getMountPoint();
};

export const useNavigateToDeploymentsList = () => {
    const setAppSubmenu = useAppGlobalState((s) => s.setAppSubmenu);
    const navigate = useNavigate();
    const { projectId } = useParams();
    const p = generatePath(getProjectSubRouteTemplate(`${PROJECT_SUBROUTE.CMC_MIGRATIONS}/${CMC_MIGRATION_SUBROUTE.DEPLOYMENTS}`), { projectId });
    return () => {
        navigate(p);
        setAppSubmenu(PROJECT_SUBROUTE.CMC_MIGRATIONS);
    };
};

export const useNavigateToDeploymentDetails = () => {
    const setAppSubmenu = useAppGlobalState((s) => s.setAppSubmenu);
    const navigate = useNavigate();
    const { projectId } = useParams();
    return (deploymentId: string) => {
        const p = generatePath(getProjectSubRouteTemplate(`${PROJECT_SUBROUTE.CMC_MIGRATIONS}/${CMC_MIGRATION_SUBROUTE.DEPLOYMENTS}/${deploymentId}`), {
            projectId,
        });
        navigate(p);
        setAppSubmenu(PROJECT_SUBROUTE.CMC_MIGRATIONS);
    };
};

export const useNavigateToDeploymentLiveLog = (deploymentId: string) => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const p = `${generateDeploymentDetailsPath(deploymentId, projectId)}/${DEPLOYMENT_DETAILS_SUBROUTE.LOG}`;
    return () => {
        navigate(p);
    };
};

export const useNavigateToDeploymentXrays = (deploymentId: string) => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const p = `${generateDeploymentDetailsPath(deploymentId, projectId)}/${DEPLOYMENT_DETAILS_SUBROUTE.XRAYS}`;
    return () => {
        navigate(p);
    };
};

export const useDownloadXray = () => {
    const { supportService } = useAppServices();
    const downloadFile = useDownloadFileFromURL();

    return async (xrayId: string) => {
        const res = await supportService.downloadXray(xrayId);
        if (res) {
            const url = res.downloadPath;
            const fileName = res.fileName;
            await downloadFile(url, fileName);
        }
    };
};

export const useNavigateToAllDeploymentsXraysScreen = () => {
    const projectId = useCurrentProjectID();
    const navigate = useNavigate();
    const p = generatePath(getProjectSubRouteTemplate(`${PROJECT_SUBROUTE.CMC_MIGRATIONS}/${CMC_MIGRATION_SUBROUTE.XRAYS}`), { projectId });
    return () => {
        navigate(p);
    };
};

export const generateDeploymentDetailsPath = (deploymentId: string, projectId: string) => {
    return generatePath(getProjectSubRouteTemplate(`${PROJECT_SUBROUTE.CMC_MIGRATIONS}/${CMC_MIGRATION_SUBROUTE.DEPLOYMENTS}/${deploymentId}`), { projectId });
};

export const formatServerAddressWithoutDefaultPort = (address: string) => {
    return address.replace(`:${PortNumbers.REMOTE_GM_SERVER}`, "");
};

export const formatServerAddressWithoutPort = (address: string) => {
    return address ? address.split(":")[0] : null;
};

export const getSystemDevPathByNamePrefix = (d: GalaxyMigrateDeploymentDetails) => {
    return d?.getInfo()?.getOsType() === MTDIDeploymentInfo.OSType.WINDOWS ? "" : "/dev/";
};

export const getHostEnvDisplayName = (e: DeploymentHostEnvironment.DeploymentHostEnvironment): string => {
    if (e === DeploymentHostEnvironment.DeploymentHostEnvironment.UNKNOWN) {
        return appI18n.t("common:status.unknown");
    }
    if (e === DeploymentHostEnvironment.DeploymentHostEnvironment.OTHER) {
        return appI18n.t("galaxymigrate:environment.onPremises");
    }
    return HOST_PLATFORMS[hostPlatformFromEnvironment(e)].name;
};
